import React from 'react';
import { EVENT_LABELS, reasonLabel } from './insuranceConfig';

// El cotizador guarda en `insurances.company` el nombre del archivo del logo
// (ej. "LogoGnp.gif"). Aquí lo limpiamos para mostrar solo "GNP".
export function normalizeCompany(raw) {
  if (!raw) return '';
  const noExt = String(raw).replace(/\.[^.]+$/, '');
  return noExt.replace(/^Logo/i, '').trim();
}

export function fmtMxn(n, withDecimals = true) {
  if (n == null || isNaN(n)) return '—';
  return '$' + Number(n).toLocaleString('en-US', {
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  });
}

export function fmtDate(iso, opts = { day: '2-digit', month: 'short', year: 'numeric' }) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX', opts); }
  catch { return String(iso); }
}

/**
 * Devuelve una etiqueta humana para un pagaré:
 *   fmtPeriodLabel(1, '2026-01-01')  ->  'Año 1 (2026)'
 *   fmtPeriodLabel(2)               ->  'Año 2'
 */
export function fmtPeriodLabel(periodNumber, startDate) {
  const n = Number(periodNumber) || 1;
  let year = null;
  if (startDate) {
    try {
      const d = new Date(startDate);
      if (!isNaN(d.getTime())) year = d.getFullYear();
    } catch { /* noop */ }
    if (year == null) {
      const m = String(startDate).match(/^(\d{4})/);
      if (m) year = parseInt(m[1], 10);
    }
  }
  return year ? `Año ${n} (${year})` : `Año ${n}`;
}

export function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso); }
}

export function getInitials(name = '') {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
}

export function ContextHeader({ cliente, vehiculo, contrato, compact = false, dark = false }) {
  const size = compact ? 40 : 48;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div className="avatar" style={{ width: size, height: size, fontSize: compact ? 13 : 15 }}>
        {getInitials(cliente || '')}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: compact ? 14 : 15, fontWeight: 600, color: dark ? '#fff' : 'var(--pei-text)' }}>
          {cliente || '—'}
        </div>
        <div style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.7)' : 'var(--pei-text-helper)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{vehiculo || ''}</span>
          {vehiculo && contrato ? <span style={{ opacity: 0.5 }}>·</span> : null}
          {contrato ? <span style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 11 }}>{contrato}</span> : null}
        </div>
      </div>
    </div>
  );
}

export function InsuranceLogo({ provider, name, size = 28 }) {
  const color = provider?.metadata?.brand_color || '#005C32';
  const display = (provider?.name || name || '—').split(' ')[0].slice(0, 3).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: 6,
      background: color, color: 'white',
      fontSize: size * 0.4, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      letterSpacing: '-0.02em', flexShrink: 0,
    }}>
      {display}
    </div>
  );
}

export function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--pei-text-helper)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: 'var(--pei-text)' }}>{value}</div>
    </div>
  );
}

export function backendEventToTimelineItem(ev) {
  const meta = EVENT_LABELS[ev.event_type] || { label: ev.event_type, icon: 'pi-circle-fill', color: 'cyan' };
  const data = ev.data || {};

  let sub = null;
  switch (ev.event_type) {
    case 'email_sent':
      sub = `A ${data.to || ''}${data.cc?.length ? ' · CC: ' + data.cc.join(', ') : ''}`;
      break;
    case 'document_uploaded':
      sub = `${data.label || 'archivo'} · ${data.file_name || ''}`;
      break;
    case 'price_discrepancy_resolved':
      sub = `Cotizador: ${fmtMxn(data.cotizador)} → Póliza: ${fmtMxn(data.pdf)} · usado: ${data.source}`;
      break;
    case 'policy_amended': {
      const diff = data.diff || {};
      const parts = Object.entries(diff).map(([k, v]) => `${k}: ${v.from} → ${v.to}`);
      sub = parts.join(' · ') + (data.reason ? ` · ${reasonLabel(data.reason)}` : '');
      break;
    }
    case 'corrected': {
      const diff = data.diff || {};
      const parts = Object.entries(diff).map(([k, v]) => `${k}: ${v.from} → ${v.to}`);
      sub = parts.length ? parts.join(' · ') : 'Sin cambios de valor';
      break;
    }
    case 'cancelled':
      sub = `${reasonLabel(data.reason)}${data.effective_at ? ` · efectiva: ${fmtDateTime(data.effective_at)}` : ''}`;
      break;
    case 'provider_rejected':
      sub = reasonLabel(data.reason);
      break;
    case 'reopened':
      sub = data.mode === 'fix' ? 'Modo corrección' : 'Modo reemplazo';
      break;
    default:
      sub = null;
  }

  return {
    icon: meta.icon,
    color: meta.color,
    title: meta.label,
    sub,
    time: fmtDateTime(ev.occurred_at),
    actor: ev.user?.email || ev.user?.name || null,
    cta: ev.document_id ? 'Ver documento' : null,
  };
}
